import React, { useState, useRef, useEffect, useCallback, useMemo, Component, ErrorInfo } from 'react'; 
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { MessageSquare, X, Mail, BrainCircuit } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import AIChatTab from './AIChatTab';
import EmailChatTab from './EmailChatTab';

// Error boundary to catch React DOM issues
class ChatErrorBoundary extends Component<{ children: React.ReactNode, onError: () => void }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode, onError: () => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Chat component error:", error, errorInfo);
    this.props.onError();
  }

  render() {
    if (this.state.hasError) {
      // Reset error state after a short delay
      setTimeout(() => {
        this.setState({ hasError: false });
      }, 500);
      
      return null; // Render nothing when an error occurs
    }

    return this.props.children;
  }
}

type ChatMode = 'info' | 'email';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  files?: File[];
}

interface Props {
  externalOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

// Simple helper to check for leftover overlays
const checkForOverlays = () => {
  // Only check for decorative hologram elements, not the backdrop which is handled by AnimatePresence
  const overlays = document.querySelectorAll('.hologram-chat, .hologram-beam, .hud-interface, .digital-energy');
  
  // Check for backdrop only if it's not part of AnimatePresence (opacity > 0 means it's not animating out)
  const backdrops = document.querySelectorAll('.backdrop-overlay');
  const activeBackdrops = Array.from(backdrops).filter((el: Element) => {
    const htmlEl = el as HTMLElement;
    const opacity = window.getComputedStyle(htmlEl).opacity;
    return parseFloat(opacity) > 0.1; // Only consider it leftover if it's visible (not animating out)
  });
  
  const allOverlays = [...Array.from(overlays), ...activeBackdrops];
  
  if (allOverlays.length > 0) {
    console.log('WARNING: Found leftover overlays in DOM after chat closed:', allOverlays);
    
    // Force remove any leftover overlays
    allOverlays.forEach(el => {
      console.log('Force removing:', el);
      el.parentNode?.removeChild(el);
    });
    
    // Double-check after removal
    setTimeout(() => {
      const remainingOverlays = document.querySelectorAll('.hologram-chat, .hologram-beam, .hud-interface, .digital-energy');
      const remainingBackdrops = document.querySelectorAll('.backdrop-overlay');
      const remainingActiveBackdrops = Array.from(remainingBackdrops).filter((el: Element) => {
        const htmlEl = el as HTMLElement;
        const opacity = window.getComputedStyle(htmlEl).opacity;
        return parseFloat(opacity) > 0.1;
      });
      
      if (remainingOverlays.length > 0 || remainingActiveBackdrops.length > 0) {
        console.log('CRITICAL: Still found overlays after forced removal:', [...Array.from(remainingOverlays), ...remainingActiveBackdrops]);
      } else {
        console.log('All overlays successfully removed');
      }
    }, 50);
  } else {
    console.log('No leftover overlays found in DOM - good!');
  }
};

// Function to handle a hard reset if needed
const forceCleanup = () => {
  document.body.style.overflow = '';
  
  // Force remove all chat-related elements
  // Only remove visible/active elements, not ones animating out via AnimatePresence
  const elementsToRemove = document.querySelectorAll('.hologram-chat, .hologram-beam, .hud-interface, .digital-energy');
  const backdrops = document.querySelectorAll('.backdrop-overlay');
  
  // Only remove backdrops that are visible (not animating out)
  const activeBackdrops = Array.from(backdrops).filter((el: Element) => {
    const htmlEl = el as HTMLElement;
    const opacity = window.getComputedStyle(htmlEl).opacity;
    return parseFloat(opacity) > 0.1;
  });
  
  [...Array.from(elementsToRemove), ...activeBackdrops].forEach(el => {
    try {
      el.parentNode?.removeChild(el);
    } catch (e) {
      console.error("Error removing element:", e);
    }
  });
  
  console.log("Forced cleanup complete");
};

// Enhanced force cleanup function at module level
function forceRemoveAllChatElements() {
  document.body.style.overflow = '';
  
  // Force remove all chat-related elements
  // Only remove visible/active elements, not ones animating out via AnimatePresence
  const elementsToRemove = document.querySelectorAll('.hologram-chat, .hologram-beam, .hud-interface, .digital-energy');
  const backdrops = document.querySelectorAll('.backdrop-overlay');
  
  // Only remove backdrops that are visible (not animating out)
  const activeBackdrops = Array.from(backdrops).filter((el: Element) => {
    const htmlEl = el as HTMLElement;
    const opacity = window.getComputedStyle(htmlEl).opacity;
    return parseFloat(opacity) > 0.1;
  });
  
  [...Array.from(elementsToRemove), ...activeBackdrops].forEach(el => {
    try {
      console.log('Removing element:', el);
      el.parentNode?.removeChild(el);
    } catch (e) {
      console.error("Error removing element:", e);
    }
  });
  
  // Make sure body scroll is enabled
  document.body.style.overflow = '';
}

// Call force cleanup on module load to clean any leftover elements
forceRemoveAllChatElements();

const AIChat: React.FC<Props> = ({ externalOpen, onOpenChange }) => {
  const { t } = useTranslation();
  const location = useLocation();
  
  // State declarations
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<ChatMode>('info');
  const [messages, setMessages] = useState<Message[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  // Initialize position to center of screen
  const [panelPosition, setPanelPosition] = useState(() => {
    if (typeof window !== 'undefined') {
      // Default: center on screen
      const chatPanelHeight = 600;
      const chatPanelWidth = 480;
      const margin = 20;
      return {
        top: Math.max(margin, (window.innerHeight - chatPanelHeight) / 2),
        left: Math.max(margin, (window.innerWidth - chatPanelWidth) / 2)
      };
    }
    // Fallback: center (not left corner!)
    return { top: 200, left: 400 };
  });
  const [errorOccurred, setErrorOccurred] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Additional refs
  const notificationSound = useRef<HTMLAudioElement | null>(null);
  const chatComponentsRef = useRef<HTMLDivElement>(null);
  const chatButtonRef = useRef<HTMLButtonElement>(null);
  const chatPanelRef = useRef<HTMLDivElement>(null);
  
  // State for proactive teaser
  const [teaserMessage, setTeaserMessage] = useState<string | null>(null);
  const teaserTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if we're in CRM - hide chat completely in CRM
  // Use useMemo to ensure it updates when location changes
  const isInCRM = useMemo(() => location.pathname.startsWith('/crm'), [location.pathname]);
  const shouldHideChat = isInCRM;
  
  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Close chat if we enter CRM
  useEffect(() => {
    if (shouldHideChat && isOpen) {
      console.log('[AIChat] Closing chat because shouldHideChat is true');
      setIsOpen(false);
      onOpenChange?.(false);
      setMode('info');
      forceRemoveAllChatElements();
    }
  }, [shouldHideChat, isOpen, onOpenChange]);
  
  // Error handler function
  const handleError = useCallback(() => {
    console.log("Error detected, performing cleanup");
    setErrorOccurred(true);
    setIsOpen(false);
    document.body.style.overflow = '';
    
    // Force cleanup DOM elements
    forceCleanup();
    
    // Reset error state after a delay
    setTimeout(() => {
      setErrorOccurred(false);
    }, 1000);
  }, []);
  
  // Log isOpen state changes for debugging
  useEffect(() => {
    console.log("isOpen changed to:", isOpen);
    
    // When isOpen changes to false, schedule a check for leftover overlays
    if (isOpen === false) {
      // Wait for AnimatePresence exit animations to complete (they take about 0.2s + buffer)
      // Increased delay to ensure AnimatePresence has fully unmounted
      setTimeout(() => {
        checkForOverlays();
      }, 500); // Reduced from 1000ms since animation is 200ms
    }
  }, [isOpen]);
  
  // Function to find the profile image and position the hologram (kept for reference)
  /*
  const positionPanel = useCallback(() => {
    try {
      // Always set a safe default position first - this ensures the chat is visible even if profile detection fails
      const defaultY = Math.max(100, Math.min(window.innerHeight - 400, window.innerHeight / 2 - 200));
      const defaultX = Math.max(10, Math.min(window.innerWidth - 410, window.innerWidth / 2 - 150));
      
      setPanelPosition({
        top: defaultY,
        left: defaultX
      });
      
      console.log("Default position set:", defaultY, defaultX);
      
      // First, check if we're on the homepage
      const profileOnHomepage = document.querySelector('.hologram-container img');
      // Or on the about page
      const profileOnAboutPage = document.querySelector('.about-profile img');
      
      let profileElement = profileOnHomepage || profileOnAboutPage;
      
      if (profileElement) {
        console.log("Profile element found");
        const rect = profileElement.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        // Check if the profile image is in the viewport or close to it
        const isInView = (
          rect.top >= -rect.height &&
          rect.left >= -rect.width &&
          rect.bottom <= (window.innerHeight + rect.height) &&
          rect.right <= (window.innerWidth + rect.width)
        );
        
        // Only use the profile-based positioning if it's visible in viewport
        if (isInView) {
          console.log("Profile is in view, positioning chat next to profile");
          
          // Calculate positions relative to the profile image
          // Use window-relative positions (fixed positioning)
          const chatXPosition = windowWidth - centerX > 350 
            ? centerX + rect.width/2 + 20 // Position to the right if there's room
            : centerX - 300 - 20; // Position to the left if not
          
          // Update position state - position it at the same level as the profile image
          setPanelPosition({
            top: Math.max(20, centerY - 200), // Position the chat higher up, with min offset from top of screen
            left: Math.max(10, Math.min(windowWidth - 310, chatXPosition)) // Ensure chat is not off-screen
          });

          // Set the decorative elements only after we're sure positioning worked
          setTimeout(() => {
            try {
              // Safely position HUD elements only if they exist
              const hudElement = document.querySelector('.hud-interface') as HTMLElement | null;
              if (hudElement) {
                hudElement.style.top = `${centerY - 60}px`;
                hudElement.style.left = `${centerX - 40}px`;
                hudElement.style.opacity = '1';
              }

              // Safely position beam element only if it exists
              const beamElement = document.querySelector('.hologram-beam') as HTMLElement | null;
              if (beamElement) {
                beamElement.style.top = `${centerY - 40}px`;
                beamElement.style.left = `${centerX}px`;
                beamElement.style.height = `${40}px`;
                beamElement.style.transform = windowWidth - centerX > 350 
                  ? 'rotate(30deg) scaleY(1)' 
                  : 'rotate(-30deg) scaleY(1)';
                beamElement.style.opacity = '0.7';
              }
              
              // Safely position energy element only if it exists
              const energyElement = document.querySelector('.digital-energy') as HTMLElement | null;
              if (energyElement) {
                const size = Math.max(rect.width, rect.height) * 1.5; // 50% larger than profile
                energyElement.style.width = `${size}px`;
                energyElement.style.height = `${size}px`;
                energyElement.style.top = `${centerY - size/2}px`;
                energyElement.style.left = `${centerX - size/2}px`;
                energyElement.style.opacity = '0.8';
              }
            } catch (err) {
              console.error("Error positioning decorative elements:", err);
            }
          }, 300);
        } else {
          console.log("Profile not in view, using default position");
          // No decorative elements in light mode
        }
      } else {
        console.log("No profile found, using default position");
      }
      
      return true;
    } catch (error) {
      console.error("Error in positionHologram:", error);
      // Use a safe default position if an error occurs
      setPanelPosition({
        top: Math.min(window.innerHeight / 2 - 200, Math.max(20, 100)),
        left: Math.min(window.innerWidth / 2 - 150, Math.max(10, window.innerWidth - 310))
      });
      return false;
    }
  }, []);
  */
  
  // Helper function to hide decorative elements (kept for future use)
  // const hideDecorativeElements = useCallback(() => {}, []);

  // Calculate optimal position for chat panel relative to button
  const calculatePanelPosition = useCallback(() => {
    const chatButton = chatButtonRef.current;
    const chatPanelHeight = 600;
    const chatPanelWidth = 480;
    const spacing = 16;
    const margin = 20;
    
    // Default: center on screen (preferred behavior)
    const centerTop = Math.max(margin, (window.innerHeight - chatPanelHeight) / 2);
    const centerLeft = Math.max(margin, (window.innerWidth - chatPanelWidth) / 2);
    
    if (!chatButton) {
      // Fallback: center on screen
      return {
        top: centerTop,
        left: centerLeft,
        anchor: 'center' as const
      };
    }

    const buttonRect = chatButton.getBoundingClientRect();
    
    // Try positioning above the button first (preferred if there's space)
    const spaceAbove = buttonRect.top;
    
    // Check if there's enough space above the button
    if (spaceAbove >= chatPanelHeight + spacing + margin) {
      // Position above button, aligned to button's right edge
      const panelTop = buttonRect.top - chatPanelHeight - spacing;
      const panelLeft = Math.min(
        buttonRect.right - chatPanelWidth, // Align to button's right edge
        window.innerWidth - chatPanelWidth - margin // But keep margin from screen edge
      );
      
      return {
        top: Math.max(margin, panelTop),
        left: Math.max(margin, panelLeft),
        anchor: 'top' as const
      };
    }
    
    // If not enough space above, center on screen (better UX than left/right)
    return {
      top: centerTop,
      left: centerLeft,
      anchor: 'center' as const
    };
  }, []);

  // Recalculate position when chat opens to ensure correct positioning
  useEffect(() => {
    if (isOpen && !errorOccurred) {
      // Small delay to ensure button ref is available
      const timer = setTimeout(() => {
        const position = calculatePanelPosition();
        setPanelPosition({ top: position.top, left: position.left });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, errorOccurred, calculatePanelPosition]);

  // Reposition on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isOpen && !errorOccurred) {
        const position = calculatePanelPosition();
        setPanelPosition({ top: position.top, left: position.left });
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, errorOccurred, calculatePanelPosition]);

  // Initialize notification sound
  useEffect(() => {
    notificationSound.current = new Audio('/sounds/notification.mp3');
  }, []);

  // Handle external open/close requests
  useEffect(() => {
    if (externalOpen !== undefined && !errorOccurred) {
      if (externalOpen) {
        openChat();
      } else {
        handleClose();
      }
    }
  }, [externalOpen, errorOccurred]);

  // Notify when open state changes
  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  // Track unread messages
  useEffect(() => {
    if (!isOpen && messages.length > 0 && messages[messages.length - 1].role === 'assistant') {
      setUnreadCount(prev => prev + 1);
      notificationSound.current?.play().catch(err => console.log('Audio play failed:', err));
    }
  }, [messages, isOpen]);

  // Proactive "Alive" behavior
  useEffect(() => {
    // Clear any existing timeout
    if (teaserTimeoutRef.current) {
      clearTimeout(teaserTimeoutRef.current);
    }

    // Only show teaser if chat is closed and no unread messages
    if (!isOpen && unreadCount === 0) {
      // Random delay between 5-15 seconds
      const delay = Math.floor(Math.random() * 10000) + 5000;
      
      teaserTimeoutRef.current = setTimeout(() => {
        // Random teaser message
        const messages = [
          t('chat.teasers.analysis', 'System diagnostics running...'),
          t('chat.teasers.optimize', 'Optimization opportunity detected'),
          t('chat.teasers.ready', 'Cortex ready. Initialize?'),
          t('chat.teasers.status', 'Infrastructure status: Active'),
        ];
        const randomMsg = messages[Math.floor(Math.random() * messages.length)];
        setTeaserMessage(randomMsg);
        
        // Play subtle sound (if desired, uncomment next line)
        // notificationSound.current?.play().catch(() => {});

        // Hide teaser after 8 seconds
        setTimeout(() => setTeaserMessage(null), 8000);
      }, delay);
    } else {
      setTeaserMessage(null);
    }

    return () => {
      if (teaserTimeoutRef.current) {
        clearTimeout(teaserTimeoutRef.current);
      }
    };
  }, [isOpen, unreadCount, t]);

  // Enhanced close handler using the global cleanup function
  const handleClose = useCallback(() => {
    console.log("handleClose called");
    
    // First set state
    setIsOpen(false);
    onOpenChange?.(false);
    setMode('info');
    
    // Ensure body scroll is restored
    document.body.style.overflow = '';
    
    // Use our enhanced cleanup function
    forceRemoveAllChatElements();
    
    // Double check after a small delay
    setTimeout(() => {
      forceRemoveAllChatElements();
      document.body.style.overflow = '';
      console.log("Scrolling restored: overflow =", document.body.style.overflow);
    }, 100);
  }, [onOpenChange]);

  // Optimized open handler that positions relative to button
  const openChat = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    
    // Don't open if we're in an error state
    if (errorOccurred) {
      return;
    }
    
    // Calculate optimal position FIRST, then set state synchronously
    const position = calculatePanelPosition();
    
    // Set position and open synchronously - React will batch these updates
    setPanelPosition({ top: position.top, left: position.left });
    setIsOpen(true);
    setUnreadCount(0); // Clear unread count immediately on open
    onOpenChange?.(true);
  }, [onOpenChange, errorOccurred, calculatePanelPosition]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = ''; // Ensure scrolling is enabled when component unmounts
      forceCleanup(); // Clean up any DOM elements
    };
  }, []);

  // Add global event listener for opening chat
  useEffect(() => {
    const handleGlobalOpen = () => {
      openChat();
    };
    
    window.addEventListener('openAIChat', handleGlobalOpen);
    
    return () => {
      window.removeEventListener('openAIChat', handleGlobalOpen);
    };
  }, [openChat]);

  // Cleanup useEffect to ensure no leftover elements when component unmounts
  useEffect(() => {
    return () => {
      // Ensure we clean up any stray elements when component unmounts
      document.body.style.overflow = '';
      forceCleanup();
    };
  }, []);
  
  // Ensure body scroll is restored when chat closes
  useEffect(() => {
    if (!isOpen) {
      // Restore scroll when chat is closed
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  // If we're in an error state, render a simpler version
  if (errorOccurred) {
    return (
      <button
        onClick={() => {
          setErrorOccurred(false);
          setTimeout(() => {
            openChat();
          }, 500);
        }}
        className="fixed bottom-4 right-4 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full shadow-lg hover:border-cyan-500 dark:hover:border-cyan-400 group"
        style={{ 
          zIndex: 9999,
          transition: 'none',
          animation: 'none'
        }}
        aria-label={t('chat.openChat')}
      >
        <MessageSquare className="w-8 h-8 text-cyan-600 dark:text-cyan-400" />
        {unreadCount > 0 && (
          <div className="absolute -top-1 -right-1 bg-cyan-500 dark:bg-cyan-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-bounce">
            {unreadCount}
          </div>
        )}
      </button>
    );
  }

  // Don't render chat button if we're in CRM on mobile
  if (shouldHideChat) {
    return null;
  }

  return (
    <ChatErrorBoundary onError={handleError}>
      {/* Chat button */}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col items-end pointer-events-none">
        <AnimatePresence>
          {teaserMessage && !isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10, x: -10 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              exit={{ opacity: 0, y: 10, x: -10 }}
              className="mb-3 mr-2 pointer-events-auto cursor-pointer"
              onClick={() => openChat()}
            >
              <div className="relative bg-[#0A0B10] border border-cyan-500/30 text-cyan-400 px-4 py-2 rounded-lg shadow-lg shadow-cyan-500/10 backdrop-blur-md">
                <div className="absolute -bottom-1.5 right-4 w-3 h-3 bg-[#0A0B10] border-r border-b border-cyan-500/30 rotate-45 transform"></div>
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
                  </span>
                  <span className="text-xs font-mono tracking-tight typing-effect">{teaserMessage}</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          ref={chatButtonRef}
          onClick={openChat}
          className="p-3 bg-[#0A0B10] border border-white/10 rounded-full shadow-lg hover:border-cyan-500 hover:shadow-cyan-500/20 group chat-button transition-all duration-200 relative pointer-events-auto"
          style={{
            zIndex: isOpen ? 10000 : 9999, // Higher z-index when chat is open
          }}
          tabIndex={0}
          aria-label={t('chat.openChat')}
          title={t('chat.openChat')}
        >
          {/* Pulse effect behind the button */}
          {!isOpen && unreadCount === 0 && (
            <span className="absolute inset-0 rounded-full bg-cyan-500/20 animate-ping-slow pointer-events-none"></span>
          )}
          
          <BrainCircuit className={`w-8 h-8 text-cyan-400 transition-transform duration-500 ${!isOpen && unreadCount === 0 ? 'group-hover:rotate-180' : ''}`} />
          
          {unreadCount > 0 && (
            <div className="absolute -top-1 -right-1 bg-cyan-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-bounce border border-[#0A0B10]">
              {unreadCount}
            </div>
          )}
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-3 py-1.5
            bg-[#0A0B10] border border-white/10 rounded text-sm text-white opacity-0 group-hover:opacity-100
            whitespace-nowrap shadow-lg pointer-events-none"
            style={{ transition: 'none' }}>
            {t('chat.openChat')}
          </span>
        </button>
      </div>

      {/* Render chat interface with proper popup positioning */}
      <AnimatePresence>
        {isOpen && (
          <div 
            ref={chatComponentsRef} 
            id="chat-container" 
            className="chat-components-container" 
            style={{ 
              display: 'block', 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              width: '100%', 
              height: '100%', 
              zIndex: 10000 
            }}
          >
            {/* Backdrop */}
            <motion.div 
              className="fixed inset-0 bg-black/60 backdrop-blur-sm backdrop-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ zIndex: 10000 }}
              onClick={handleClose}
              data-testid="chat-backdrop"
            />
            
            {/* Chat Panel - positioned relative to button */}
            <motion.div
              ref={chatPanelRef}
              className="fixed w-full max-w-[480px] bg-[#04060b] text-slate-200
                rounded-xl shadow-2xl overflow-hidden flex flex-col border border-white/10"
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ 
                opacity: 1, 
                scale: 1, 
                y: 0,
                top: isMobile ? '1rem' : `${panelPosition.top}px`,
                left: isMobile ? '1rem' : `${panelPosition.left}px`,
                right: isMobile ? '1rem' : 'auto',
                bottom: isMobile ? '1rem' : 'auto'
              }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ 
                duration: 0.2,
                ease: [0.4, 0, 0.2, 1] // Custom easing for smooth animation
              }}
              style={{
                height: isMobile ? "calc(100vh - 2rem)" : "600px",
                maxHeight: isMobile ? "calc(100vh - 2rem)" : "600px",
                maxWidth: isMobile ? "calc(100vw - 2rem)" : "480px",
                width: isMobile ? "calc(100vw - 2rem)" : "100%",
                zIndex: 10001, // Above backdrop and button
                position: 'fixed',
                pointerEvents: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Chat Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 bg-[#04060b]">
                <button className="flex items-center gap-4 group/header" onClick={() => setMode('info')}>
                  <div className="relative">
                    <div className="absolute inset-0 bg-cyan-500/20 blur-md rounded-full group-hover/header:bg-cyan-500/30 transition-all" />
                    <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-slate-800 to-black border border-white/10 flex items-center justify-center shadow-xl">
                      <BrainCircuit className="w-5 h-5 text-cyan-400" />
                    </div>
                  </div>
                  <div className="flex flex-col items-start gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-white tracking-wide font-mono">CORTEX_OS</span>
                      <span className="flex h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)] animate-pulse" />
                    </div>
                    <span className="text-[10px] text-slate-500 uppercase tracking-widest font-mono group-hover/header:text-cyan-400 transition-colors">
                      v2.4.0 • SYSTEM ONLINE
                    </span>
                  </div>
                </button>

                <div className="flex items-center gap-2">
                  <button
                    className={`p-2 rounded-lg border transition-all duration-300 ${
                      mode === 'email'
                        ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20 shadow-[0_0_10px_rgba(6,182,212,0.1)]'
                        : 'text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/5'
                    }`}
                    onClick={() => setMode(mode === 'email' ? 'info' : 'email')}
                    aria-label="Send Email"
                  >
                    <Mail className="w-4 h-4" />
                  </button>
                  <div className="w-px h-4 bg-white/10 mx-1" />
                  <button
                    onClick={handleClose}
                    className="p-2 text-slate-500 hover:text-white rounded-lg hover:bg-white/5 transition-colors border border-transparent hover:border-white/5"
                    aria-label="Close chat"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Chat Content - Different based on mode */}
              <div className="flex-1 flex flex-col overflow-hidden bg-[#04060b]">
                {mode === 'info' ? (
                  <AIChatTab 
                    messages={messages} 
                    setMessages={setMessages} 
                  />
                ) : (
                  <EmailChatTab onClose={handleClose} />
                )}
              </div>
                
              {/* Subtle corner accents */}
              <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-cyan-500/10 rounded-tl-2xl pointer-events-none"></div>
              <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-purple-500/10 rounded-br-2xl pointer-events-none"></div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </ChatErrorBoundary>
  );
};

export default AIChat;
